"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";

export function LandingNav() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY.current;

      if (currentScrollY < 10) {
        setIsVisible(true);
      } else if (delta > 10) {
        setIsVisible(false);
      } else if (delta < -10) {
        setIsVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const centerLinks = [
    { label: "How it Works", href: "/#how-it-works" },
    { label: "Security", href: "/security" },
    { label: "Contact", href: "/#contact" },
  ];

  const shouldShow = isVisible || isMobileMenuOpen;

  return (
    <motion.nav
      animate={{ y: shouldShow ? 0 : "-100%" }}
      transition={{ duration: 0.3 }}
      className="fixed top-0 w-full z-50 bg-landing-bg/80 backdrop-blur-xl shadow-[0_12px_32px_rgba(1,22,56,0.08)]"
    >
      <div className="flex items-center px-8 py-4 max-w-7xl mx-auto">
        {/* Logo — left */}
        <Link href="/" className="flex items-center gap-2">
          <Image
            src="/logos/dezu-icon-black.png"
            alt="Dezu"
            width={28}
            height={28}
          />
          <span className="text-2xl font-bold tracking-tighter text-landing-text">
            Dezu
          </span>
        </Link>

        {/* Center links */}
        <div className="hidden md:flex items-center justify-center flex-1 space-x-8">
          {centerLinks.map((link) => (
            <Link
              key={link.label}
              href={link.href}
              className="font-medium text-sm tracking-tight text-landing-text-secondary hover:text-landing-primary transition-colors duration-200"
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right — Login + Get a Demo */}
        <div className="hidden md:flex items-center gap-6">
          <Link
            href="/login"
            className="font-medium text-sm text-landing-text-secondary hover:text-landing-text transition-colors"
          >
            Log In
          </Link>
          <Link
            href="https://cal.com/riyadev/15min"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-landing-primary text-white px-6 py-2 rounded-full font-medium text-sm hover:scale-105 active:scale-95 transition-all"
          >
            Get a Demo
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-landing-text"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMobileMenuOpen}
        >
          <span className="material-symbols-outlined">
            {isMobileMenuOpen ? "close" : "menu"}
          </span>
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 w-full bg-landing-bg shadow-lg md:hidden border-t border-landing-text-secondary/5"
          >
            <nav className="flex flex-col px-8 py-4 gap-4">
              {centerLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="text-base font-medium text-landing-text-secondary hover:text-landing-primary transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                href="/login"
                className="text-base font-medium text-landing-text-secondary hover:text-landing-text transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Log In
              </Link>
              <Link
                href="https://cal.com/riyadev/15min"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-landing-primary text-white px-6 py-3 rounded-full font-medium text-sm text-center hover:bg-landing-primary-hover transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Get a Demo
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
